import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { columnFilterSchema } from "../../../common/columnFilterSchema";
import { cursorSchema } from "../../../common/cursorSchema";
import { productReportGroupingSchema } from "../../../common/productReportGroupingSchema";
import { statsSchema, merchStatsSchema } from "../../../common/statsSchema";
import { tagsSchema, tagsEnhancedSchema } from "../../../common/tagsSchema";

const productStatsSchema = z.object({
  bsr_rank: z.number().nullable(),
  bsr_rank_prev: z.number().nullable(),
  bsr_rank_delta: z.number().nullable(),
  bsr_rank_percent: z.number().nullable(),

  trends_score: z.number().nullable(),
  sales_trend: z.number().nullable(),
  margin_trend: z.number().nullable(),

  profit_per_page_view: z.number().nullable(),
  profit_per_page_view_prev: z.number().nullable(),
  profit_per_page_view_delta: z.number().nullable(),
  profit_per_page_view_percent: z.number().nullable(),

  profit_per_page_view_wo_adspend: z.number().nullable(),
  profit_per_page_view_wo_adspend_prev: z.number().nullable(),
  profit_per_page_view_wo_adspend_delta: z.number().nullable(),
  profit_per_page_view_wo_adspend_percent: z.number().nullable(),

  total_order_count: z.number().nullable(),
  total_order_count_prev: z.number().nullable(),
  total_order_count_delta: z.number().nullable(),
  total_order_count_percent: z.number().nullable(),

  seo_score: z.number().nullable(),
  seo_score_prev: z.number().nullable(),
  seo_score_delta: z.number().nullable(),
  seo_score_percent: z.number().nullable(),

  buyability_score: z.number().nullable(),
  buyability_score_prev: z.number().nullable(),
  buyability_score_delta: z.number().nullable(),
  buyability_score_percent: z.number().nullable(),

  product_price: z.number().nullable(),
});

const pricingPlanStatusSchema = z.enum(["not_enrolled", "enabled", "disabled", ""]).nullable();

/** Product fields - all required (used when grouping by product_id) */
const productFieldsSchema = z.object({
  product_id: z.number(),
  product_title: z.string(),
  product_key: z.string(),
  asin: z.string(),
  sku: z.string(),
  sales_state: z.string(),
  inventory_state: z.string(),
  stock_status: z.string(),
  category_id: z.number(),
  product_quantity: z.number(),
  product_quantity_fba: z.number(),
  product_price: z.number(),
  min_price: z.number().nullable(),
  max_price: z.number().nullable(),
  parent_asin: z.string().nullable(),
  parent_asin_defaulted: z.string().nullable(),
  primary_variant: z.boolean().nullable(),
  pricing_plan_status: pricingPlanStatusSchema,
  price_override_end: z.string().nullable(),
  has_ads: z.boolean(),
  tags: tagsSchema,
  tags_enhanced: tagsEnhancedSchema,
});

/** Product fields - all optional (used when grouping by asin/parent_asin) */
const productFieldsPartialSchema = productFieldsSchema.partial();

export const productReportApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4ProductReportByProduct",
    method: "get",
    path: "/v4/query/?table=product&grouping=product_id",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "filters",
        type: "Query",
        schema: columnFilterSchema,
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(productFieldsSchema.merge(statsSchema).merge(merchStatsSchema).merge(productStatsSchema)),
      cursor: cursorSchema,
      count: z.number(),
    }),
  })
    .addEndpoint({
      alias: "getV4ProductReportByGrouping",
      method: "get",
      path: "/v4/query/?table=product",
      parameters: [
        {
          name: "grouping",
          type: "Query",
          schema: productReportGroupingSchema,
        },
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
        {
          name: "cursor",
          type: "Query",
          schema: z.number().optional(),
        },
        {
          name: "limit",
          type: "Query",
          schema: z.number().optional(),
        },
        {
          name: "ordering",
          type: "Query",
          schema: z.string().optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(productFieldsPartialSchema.merge(statsSchema).merge(merchStatsSchema).merge(productStatsSchema)),
        cursor: cursorSchema,
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4ProductReportByDate",
      method: "get",
      path: "/v4/query/?table=product&grouping=start_date&limit=1000000&include_count=false",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              start_date: z.string().date(),
            })
            .merge(statsSchema)
            .merge(merchStatsSchema)
            .merge(productStatsSchema),
        ),
        cursor: cursorSchema,
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4ProductReportTotals",
      method: "get",
      path: "/v4/query/aggregate/?table=product",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: statsSchema.merge(merchStatsSchema).merge(productStatsSchema),
      }),
    })
    .addEndpoint({
      alias: "getV4ProductReportOptions",
      method: "get",
      path: "/v4/query/values/?table=product",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.object({
          tags: z.array(z.string()),
          variations: z.record(z.array(z.string())), // Dynamic object with keys mapping to arrays of strings
        }),
      }),
    })
    .addEndpoint({
      alias: "getV4ProductReportExport",
      method: "get",
      path: "/v4/query/?table=product&xls=true&report_type=product_report",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .build(),
);
